import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Layers, RefreshCw, Code2, Network, Rocket, TrendingUp, LifeBuoy, ClipboardCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { getServices } from "@/lib/api";

const ICONS = {
  "implementation": Layers,
  "migrations": RefreshCw,
  "application-development": Code2,
  "integration": Network,
  "rollouts": Rocket,
  "upgrades": TrendingUp,
  "support-maintenance": LifeBuoy,
  "testing": ClipboardCheck,
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getServices()
      .then((s) => { if (mounted) setServices(s); })
      .catch(() => { if (mounted) setServices([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  return (
    <div data-testid="services-page">
      <section className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-8">
              <div className="overline">Services</div>
              <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-[1.05] mt-6 text-[#0F172A]">
                Eight SAP disciplines, <br/> delivered end-to-end.
              </h1>
              <p className="text-lg text-gray-600 mt-8 max-w-2xl leading-relaxed">
                Each engagement is led by a senior partner and staffed with consultants who have shipped — not just shadowed — comparable programs.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-24" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-16">
          {loading && <div className="text-gray-500" data-testid="services-loading">Loading services…</div>}
          {services.map((s, i) => {
            const Icon = ICONS[s.slug] || Layers;
            return (
              <Reveal key={s.slug} delay={i * 80}>
                <article
                  data-testid={`service-detail-${s.slug}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-10 border-t border-[#111827] pt-10"
                >
                  <div className="md:col-span-3">
                    <div className="font-mono text-sm text-gray-500">0{i + 1}</div>
                    <Icon size={36} strokeWidth={1.3} className="mt-6 text-[#111827]" />
                  </div>
                  <div className="md:col-span-6">
                    <h2 className="font-serif text-3xl sm:text-4xl text-[#0F172A] leading-tight">{s.title}</h2>
                    <p className="text-gray-600 mt-5 text-lg leading-relaxed">{s.summary}</p>
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {(s.highlights || []).map((h) => (
                        <div key={h} className="flex items-start gap-3">
                          <CheckCircle2 size={18} className="text-[#111827] mt-0.5" />
                          <span className="text-[15px] text-gray-700">{h}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="md:col-span-3 md:text-right">
                    <Link to="/contact" data-testid={`service-cta-${s.slug}`} className="link-underline inline-flex items-center gap-2 text-[#111827] font-medium">
                      Talk to a partner <ArrowUpRight size={16} />
                    </Link>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>
    </div>
  );
}
